import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Scan,
  Bold,
  Underline,
  Strikethrough,
  Italic,
  AlignVerticalSpaceAround,
  X,
} from "lucide-react";

const fontFamilies = [
  "Arial, sans-serif",
  "'Courier New', monospace",
  "'Times New Roman', serif",
  "'Comic Sans MS', cursive",
  "'Roboto', sans-serif",
];

const selectTypes = ["circle", "square", "diamond"];

const MCSingleSelectToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [fontSizeInput, setFontSizeInput] = useState(
    String(selectedComponent?.fontSize ?? 14)
  );
  const [optionSpacingInput, setOptionSpacingInput] = useState(
    String(selectedComponent?.optionSpacing ?? 8)
  );
  const [optionBorderRadiusInput, setOptionBorderRadiusInput] = useState(
    String(selectedComponent?.optionBorderRadius ?? 8)
  );
  const [optionBorderWidthInput, setOptionBorderWidthInput] = useState(
    String(selectedComponent?.optionBorderWidth ?? 1)
  );

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  }, [selectedComponent?.fontSize]);

  useEffect(() => {
    setOptionSpacingInput(String(selectedComponent?.optionSpacing ?? 8));
  }, [selectedComponent?.optionSpacing]);

  useEffect(() => {
    setOptionBorderRadiusInput(
      String(selectedComponent?.optionBorderRadius ?? 8)
    );
  }, [selectedComponent?.optionBorderRadius]);

  useEffect(() => {
    setOptionBorderWidthInput(
      String(selectedComponent?.optionBorderWidth ?? 1)
    );
  }, [selectedComponent?.optionBorderWidth]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
      };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0;
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";

    return {
      left: leftOffset,
      transform: "translateX(-50%)",
    };
  };

  const position = calculatePosition();

  const toggleFontStyle = (key) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      fontStyles: {
        ...selectedComponent.fontStyles,
        [key]: !selectedComponent.fontStyles?.[key],
      },
    });
  };

  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitFontSize = () => {
    const clamped = clampNumber(fontSizeInput, 8, 48, 14);
    setFontSizeInput(String(clamped));
    if (selectedComponent?.fontSize !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        fontSize: clamped,
      });
    }
  };
  const resetFontSizeInput = () =>
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));

  const commitOptionSpacing = () => {
    const clamped = clampNumber(optionSpacingInput, 0, 50, 8);
    setOptionSpacingInput(String(clamped));
    if (selectedComponent?.optionSpacing !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        optionSpacing: clamped,
      });
    }
  };
  const resetOptionSpacingInput = () =>
    setOptionSpacingInput(String(selectedComponent?.optionSpacing ?? 8));

  const commitOptionBorderRadius = () => {
    const clamped = clampNumber(optionBorderRadiusInput, 0, 50, 8);
    setOptionBorderRadiusInput(String(clamped));
    if (selectedComponent?.optionBorderRadius !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        optionBorderRadius: clamped,
      });
    }
  };
  const resetOptionBorderRadiusInput = () =>
    setOptionBorderRadiusInput(
      String(selectedComponent?.optionBorderRadius ?? 8)
    );

  const commitOptionBorderWidth = () => {
    const clamped = clampNumber(optionBorderWidthInput, 0, 10, 1);
    setOptionBorderWidthInput(String(clamped));
    if (selectedComponent?.optionBorderWidth !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        optionBorderWidth: clamped,
      });
    }
  };
  const resetOptionBorderWidthInput = () =>
    setOptionBorderWidthInput(
      String(selectedComponent?.optionBorderWidth ?? 1)
    );

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`bg-white rounded-xl shadow-xl border border-gray-200 relative`}
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "600px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-2 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        <div className={`${isMobile ? "px-2 py-3" : "px-4 py-4"} w-full`}>
          <div className="flex flex-col md:flex-row gap-3">
            {/* Font Family & Size */}
            <div className="flex flex-col gap-2 flex-1">
              <p className="text-xs text-black font-semibold mb-1">
                Text Options
              </p>
              <div className="flex gap-1">
                <label className="flex-1">
                  <select
                    value={selectedComponent?.fontFamily || fontFamilies[0]}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontFamily: e.target.value,
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fontFamilies.map((f) => (
                      <option key={f} value={f}>
                        {f.split(",")[0]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="w-12 md:w-10">
                  <input
                    type="number"
                    min={8}
                    max={48}
                    value={fontSizeInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFontSizeInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 8 &&
                        parsed <= 48 &&
                        selectedComponent?.fontSize !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontSize: parsed,
                        });
                      }
                    }}
                    onBlur={commitFontSize}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitFontSize();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetFontSizeInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
              {/* Font Style Toggles */}
              <div className="flex gap-1">
                {["bold", "italic", "underline", "lineThrough"].map((style) => {
                  const iconMap = {
                    bold: <Bold className="h-5 w-5" />,
                    italic: <Italic className="h-5 w-5" />,
                    underline: <Underline className="h-5 w-5" />,
                    lineThrough: <Strikethrough className="h-5 w-5" />,
                  };
                  const titleMap = {
                    bold: "Bold",
                    italic: "Italic",
                    underline: "Underline",
                    lineThrough: "Strikethrough",
                  };
                  const isActive = Boolean(
                    selectedComponent?.fontStyles?.[style]
                  );
                  return (
                    <button
                      key={style}
                      onClick={() => toggleFontStyle(style)}
                      className={`w-8 h-8 rounded border text-sm font-semibold flex items-center justify-center select-none
                      ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                      title={titleMap[style]}
                      aria-pressed={isActive}
                      type="button"
                    >
                      {iconMap[style]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {/* Option Spacing */}
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Option Spacing
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <AlignVerticalSpaceAround className="h-4 w-4" />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={selectedComponent?.optionSpacing || 8}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          optionSpacing: +e.target.value,
                        })
                      }
                      className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>
                {/* Option Background Color */}
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Items Background Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow">
                    <input
                      type="color"
                      value={
                        selectedComponent?.optionBackgroundColor || "#f9fafb"
                      }
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          optionBackgroundColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>
                {/* Option Border Color  */}
                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Border Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow ">
                    <input
                      type="color"
                      value={selectedComponent?.optionBorderColor || "#d1d5db"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          optionBorderColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {/* Option Border Radius */}
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Corner Radius
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <Scan className="h-5 w-5" />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={optionBorderRadiusInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setOptionBorderRadiusInput(next);
                        const parsed = parseInt(next, 10);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 0 &&
                          parsed <= 50 &&
                          selectedComponent?.optionBorderRadius !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            optionBorderRadius: parsed,
                          });
                        }
                      }}
                      onBlur={commitOptionBorderRadius}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitOptionBorderRadius();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetOptionBorderRadiusInput();
                        }
                      }}
                      className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>
                {/* Select Type */}
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Select Type
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <select
                      value={selectedComponent?.selectType || selectTypes[0]}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          selectType: e.target.value,
                        })
                      }
                      className="w-full border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    >
                      {selectTypes.map((type) => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>
                {/* Main Background Color */}
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Background Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow ">
                    <input
                      type="color"
                      value={selectedComponent?.backgroundColor || "#ffffff"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          backgroundColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>
                {/* Option Border Width */}
                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Border Width
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <Scan className="h-5 w-5" />
                    <input
                      type="number"
                      min={0}
                      max={10}
                      value={optionBorderWidthInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setOptionBorderWidthInput(next);
                        const parsed = parseInt(next, 10);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 0 &&
                          parsed <= 10 &&
                          selectedComponent?.optionBorderWidth !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            optionBorderWidth: parsed,
                          });
                        }
                      }}
                      onBlur={commitOptionBorderWidth}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitOptionBorderWidth();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetOptionBorderWidthInput();
                        }
                      }}
                      className="w-8 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MCSingleSelectToolbar;
